import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Box
} from '@mui/material';
import {
  WhatsApp,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh
} from '@mui/icons-material';
import { getWhatsAppStatus } from '../redux/actions/communicationActions';

const WhatsAppStatusIndicator = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { whatsappStatus } = useSelector((state) => state.communication || { whatsappStatus: {} });

  useEffect(() => {
    // Fetch status on mount
    dispatch(getWhatsAppStatus());
    
    // Refresh status every 30 seconds
    const interval = setInterval(() => {
      dispatch(getWhatsAppStatus());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleRefresh = () => {
    dispatch(getWhatsAppStatus());
  };

  const handleClick = () => {
    navigate('/communication');
  };

  const getStatusColor = () => {
    if (whatsappStatus.loading) return 'default';
    if (whatsappStatus.connected || whatsappStatus.status === 'ready' || whatsappStatus.isReady) return 'success';
    if (whatsappStatus.status === 'not_configured') return 'default';
    if (whatsappStatus.status === 'error') return 'error';
    return 'warning';
  };

  const getStatusIcon = () => {
    if (whatsappStatus.loading) {
      return <CircularProgress size={16} sx={{ color: 'inherit' }} />;
    }
    if (whatsappStatus.connected || whatsappStatus.status === 'ready' || whatsappStatus.isReady) {
      return <CheckCircle sx={{ fontSize: 16 }} />;
    }
    if (whatsappStatus.status === 'error') {
      return <ErrorIcon sx={{ fontSize: 16 }} />;
    }
    return <Warning sx={{ fontSize: 16 }} />;
  };

  const getStatusText = () => {
    if (whatsappStatus.loading) return 'Checking...';
    if (whatsappStatus.connected || whatsappStatus.status === 'ready' || whatsappStatus.isReady) {
      return whatsappStatus.mode === 'api' ? 'WhatsApp API Connected' : 'WhatsApp Web Connected';
    }
    if (whatsappStatus.status === 'not_configured') return 'Not Configured';
    if (whatsappStatus.status === 'disconnected') return 'Disconnected';
    if (whatsappStatus.status === 'connecting') return 'Connecting...';
    if (whatsappStatus.status === 'qr') return 'Scan QR Code';
    return whatsappStatus.message || 'Unknown Status';
  };

  const getTooltipText = () => {
    if (whatsappStatus.loading) return 'Checking WhatsApp connection status...';
    if (whatsappStatus.connected || whatsappStatus.status === 'ready' || whatsappStatus.isReady) {
      return `WhatsApp is connected (${whatsappStatus.mode === 'api' ? 'API Mode' : 'Web Mode'}). Click to manage settings.`;
    }
    return `${whatsappStatus.message || 'WhatsApp is not connected'}. Click to configure.`;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Tooltip title={getTooltipText()} arrow>
        <Chip
          icon={getStatusIcon()}
          label={getStatusText()}
          color={getStatusColor()}
          size="small"
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8
            },
            height: '28px',
            fontSize: '0.75rem',
            fontWeight: 500
          }}
        />
      </Tooltip>
      <Tooltip title="Refresh Status" arrow>
        <span>
          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={whatsappStatus.loading}
            sx={{
              color: 'white',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)'
              },
              '&.Mui-disabled': {
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            <Refresh sx={{ fontSize: 18 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default WhatsAppStatusIndicator;

